import {
  FormHelperText,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../components/Custom3dotString";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomUpload from "../../../components/CustomUpload";
import CustomSelect from "../../../components/Select/Select";
import { soapApi } from "../../../redux/actions/api";
import {
  createOrderInvoice,
  getStockByIdWoLoader,
  updateStockDetailsById,
} from "../../../redux/reducers/clinicSlice";
import { hideLoader, showLoader } from "../../../redux/reducers/loaderSlice";
import { administeredUOMList, purchaseTypes } from "../../../util/dropList";
import {
  getExactNumber,
  getTotalValues,
  normalizeDate,
} from "../../../util/function";
import { tblGrayBack } from "../../../util/object";
import FilesWithCloseButton from "../../Hospital/ClinicPetDetails/PatientWorkbook/FilesWithCloseButton";

const reqTxt = "This field is required";
const initObj = {
  hsnSacCode: "",
  name: "",
  quantity: "1",
  packageUom: "",
  purchaseUom: null,
  gst: 0,
  cgst: 0,
  sgst: 0,
  taxableValue: 0,
  taxes: 0,
  itemTotal: 0,
};
const initStckVal = {
  supplierName: "",
  billDate: null,
  invoiceNumber: "",
  grnNumber: "",
  list: [],
  purchaseType: { name: "Intrastate", value: "Intrastate" },
};
const stckRequiredFields = ["supplierName", "invoiceNumber"];
const stckEntTblHdrs = [
  "sno",
  "name",
  "hsnSacCode",
  "batchNumber",
  "expiryDate",
  "quantity",
  "purchaseUom",
  "mrp",
  "taxableValue",
  "discountPercentage",
  "cgst",
  "sgst",
  // "taxes",
  "itemTotal",
  "status",
];
const stckEntIgstTblHdrs = [
  "sno",
  "name",
  "hsnSacCode",
  "batchNumber",
  "expiryDate",
  "quantity",
  "purchaseUom",
  "mrp",
  "taxableValue",
  "discountPercentage",
  "gst",
  // "taxes",
  "itemTotal",
  "status",
];
const stckNmeExpan = {
  icon: "",
  sno: "S No",
  hsnSacCode: "HSN Code",
  name: "Name *",
  quantity: "Received Qty *",
  cgst: "CGST % *",
  sgst: "SGST % *",
  gst: "IGST % *",
  taxableValue: "Rate *",
  // taxableValue: "Taxable Value *",
  taxes: "Taxes *",
  itemTotal: "Item Total *",
  purchaseUom: "UOM *",
  mrp: "MRP",
  batchNumber: "Batch Number",
  expiryDate: "Expiry Date",
  discountPercentage: "Disc %",
};

const PurchaseInvoiceCreateView = ({
  modVsble,
  onCloseMod = () => {},
  title = "Purchase Invoice",
  step = 1,
  hideManualEntry = false,
  type = "Invoice",
  selectedInvoice = null,
}) => {
  const dispatch = useDispatch();
  const [actStep, setActStep] = useState(step ?? 1);
  const [stcEntFiles, setStcEntFiles] = useState([]);
  const [stockEntrErr, setStockEntErr] = useState(false);
  const [stckValue, setStckValue] = useState(initStckVal);
  const [isListNotValid, setListNotValid] = useState(false);
  const [stckErrs, setStckErrs] = useState({});
  const [stckHelps, setStckHelps] = useState({});
  const [stcEntModVsble, setStcEntModVsble] = useState(false);
  const [submitErr, setSubmitErr] = useState(false);
  const { suppliers, selectedClinicBranch, allSuppliers } = useSelector(
    (state) => state?.clinic,
  );
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { stocks: clinicStocks } = useMemo(() => {
    if (!mandateDataRaw) {
      return { stocks: [] };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
      };
    } catch {
      return { stocks: [] };
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    if (selectedInvoice) appendTableValues();
  }, [selectedInvoice]);

  const appendTableValues = () => {
    const reqPurType = purchaseTypes?.find(
      (li) =>
        li?.value?.toLowerCase() ===
        (selectedInvoice?.purchaseType ?? "")?.toLowerCase(),
    );
    const reqList = (selectedInvoice?.supplierInvoiceDetails ?? [])?.map(
      (li) => {
        const reqUom = administeredUOMList?.find(
          (al) =>
            al?.value?.toLowerCase() ===
            (li?.uom ?? li?.purchaseUom ?? "")?.toLowerCase(),
        );
        return {
          invoiceDetailId: li?.invoiceDetailId ?? null,
          hsnSacCode: li?.hsnSacCode ?? "",
          name: li?.tradeName ?? li?.name ?? "",
          quantity: li?.quantity,
          packageUom: "",
          purchaseUom: reqUom ? reqUom : null,
          gst: Number(li?.cgst ?? 0) * 2,
          igst: Number(li?.cgst ?? 0) * 2,
          gst: li?.gst,
          cgst: li?.cgst,
          sgst: li?.sgst,
          mrp: li?.mrp,
          expiryDate: li?.expiryDate ? li?.expiryDate : null,
          stockId: li?.stockId ?? null,
          batchNumber: li?.batchNumber,
          taxableValue: li?.taxableValue,
          taxes: li?.taxes,
          itemTotal: li?.itemTotal,
          landedCost: li?.landedCost,
          discountPercentage: li?.discountPercentage ?? 0,
        };
      },
    );
    const reqObj = {
      supplierName: selectedInvoice?.supplierName,
      supplierId: selectedInvoice?.supplierId ?? null,
      billDate: new Date(selectedInvoice?.billDate),
      invoiceNumber: selectedInvoice?.invoiceNumber,
      poNumber: selectedInvoice?.poNumber,
      grnNumber: selectedInvoice?.grnNumber,
      GSTIN: selectedInvoice?.gstin ?? "",
      list: reqList,
      purchaseType: reqPurType ? reqPurType : null,
      supplierId: selectedInvoice?.supplierId ?? null,
      additionalCharges: selectedInvoice?.additionalCharges,
      email: selectedInvoice?.email ?? "",
      phone: selectedInvoice?.phone ?? "",
      address: selectedInvoice?.address ?? "",
      totalTaxableValue: selectedInvoice?.totalTaxableValue ?? 0,
      totalTaxes: selectedInvoice?.totalTaxes ?? 0,
      grandTotal: selectedInvoice?.grandTotal ?? 0,
      type: selectedInvoice?.type,
      creditSave: selectedInvoice?.creditSave,
    };
    setStckValue(reqObj);
    setActStep(2);
  };

  const handleChangeAdditionalCharge = (name, value) => {
    setStckValue({ ...stckValue, [name]: value });
  };

  const handleManualAddEntry = () => {
    setActStep(2);
    setStcEntModVsble(true);
  };

  const getStockList = () => {
    const reqList = clinicStocks
      ?.filter((stc) => stc?.active === "Y")
      ?.map((li) => ({
        name: li?.tradeName ?? li?.productName,
        value: li?.tradeName ?? li?.productName,
        stockId: li?.stockId,
        type: li?.category,
        productName: li?.productName,
        tradeName: li?.tradeName,
      }));
    return reqList ?? [];
  };

  const onEntFileUpload = (e) => {
    const fileList = e?.target?.files;
    let fileLists = stcEntFiles?.length > 0 ? [...stcEntFiles] : [];

    const readFiles = Array.from(fileList).map((file, i) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          fileLists = [
            ...fileLists,
            {
              file: file,
              previewUrl: reader.result,
              i,
            },
          ];
          resolve();
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readFiles)
      .then(() => {
        setStcEntFiles(fileLists);
        setListNotValid(fileLists?.length > 0 ? false : true);
      })
      .catch((error) => console.error("Error reading files: ", error));
  };

  const handleImageRemove = (ind) => {
    const reqList = stcEntFiles
      ?.filter((fle, i) => i !== ind)
      ?.map((fle, i) => ({ ...fle, i }));
    setStcEntFiles(reqList);
    setListNotValid(reqList?.length > 0 ? false : true);
  };

  const getSupplierList = () => {
    const reqList = (allSuppliers?.data ?? [])
      ?.map((sup) => ({
        name: sup?.supplierName,
        value: sup?.supplierName,
        supplierId: sup?.supplierId,
        gstNumber: sup?.gstNumber,
        email: sup?.email,
        phone: sup?.phone,
      }))
      ?.sort((a, b) => a?.name?.localeCompare(b?.name));
    return reqList;
  };

  const handleChangeStckValue = (name, value, addObj) => {
    const reqObj = { ...stckValue, [name]: value };
    const reqValue = typeof value === "object" ? value?.value : value;
    if (name === "supplierName" && addObj?.supplierId) {
      reqObj.GSTIN = addObj?.gstNumber ?? null;
      reqObj.email = addObj?.email ?? null;
      reqObj.phone = addObj?.phone ?? null;
      reqObj.supplierId = addObj?.supplierId ?? null;
    }
    const reqErrs = {
      ...stckErrs,
      [name]:
        reqValue?.length > 0
          ? false
          : stckRequiredFields?.includes(name)
            ? true
            : false,
    };

    const reqHelps = {
      ...stckHelps,
      [name]:
        reqValue?.length > 0
          ? ""
          : stckRequiredFields?.includes(name)
            ? "This is required field"
            : "",
    };
    if (name === "billDate") {
      if (value === null) {
        reqErrs[name] = true;
        reqHelps[name] = "This is required field";
      } else {
        reqErrs[name] = false;
        reqHelps[name] = "";
      }
    }
    if (name === "purchaseType") {
      reqObj.list = stckValue?.list?.map((li) => {
        const obj = {
          ...li,
          cgst:
            value?.value === "Intrastate"
              ? Number(li?.gst ?? li?.igst ?? 0) / 2
              : 0,
          sgst:
            value?.value === "Intrastate"
              ? Number(li?.gst ?? li?.igst ?? 0) / 2
              : 0,
          igst:
            value?.value === "Interstate"
              ? Number(li?.gst ?? li?.igst ?? 0)
              : 0,
        };
        if (value?.value === "Exempt") {
          obj.taxes = 0;
          obj.itemTotal = Number(li?.taxableValue) * Number(li?.quantity);
        }
        if (value?.value !== "Exempt") {
          const reqTaxes = Number(li?.taxableValue) * (li?.gst / 100);
          obj.taxes = reqTaxes * (li?.quantity ?? 0);
          obj.itemTotal =
            (Number(li?.taxableValue) + Number(reqTaxes)) * (li?.quantity ?? 0);
        }
        return obj;
      });
    }
    setStckValue(reqObj);
    setStckHelps(reqHelps);
    setStckErrs(reqErrs);
  };

  const handleChangeTableValue = (name, value, ind, addObj) => {
    const purchaseType = stckValue?.purchaseType?.value;
    const reqList = stckValue?.list?.map((li, i) => {
      if (i === ind) {
        const reqObj = {
          ...li,
          [name]: value,
          errors: { ...(li.errors || {}), [name]: undefined },
        };
        if (name === "name" && addObj?.stockId) {
          reqObj.stockId = addObj.stockId;
          dispatch(getStockByIdWoLoader(addObj?.stockId)).then((res) => {
            if (res?.payload) {
              const reqObj = res?.payload;
              const reqLatData = reqObj?.stockBatch?.[0] ?? {};
              const uomMat = administeredUOMList?.find(
                (al) =>
                  al?.value?.toLowerCase() ===
                  (
                    reqLatData?.qtyMapName ??
                    reqLatData?.uomMapName ??
                    ""
                  )?.toLowerCase(),
              );
              setStckValue((prev) => ({
                ...prev,
                list: prev?.list?.map((row, rowInd) => {
                  if (rowInd !== ind) return row;
                  const cgst = reqLatData?.cgst ?? 0;
                  const sgst = reqLatData?.sgst ?? 0;
                  const gst = Number(cgst) * 2;
                  const updated = {
                    ...row,
                    stockId: addObj?.stockId,
                    cgst,
                    sgst,
                    gst,
                    purchaseUom: uomMat ?? row?.purchaseUom ?? null,
                    hsnSacCode: reqObj?.hsnSacCode ?? "",
                    mrp: reqLatData?.unitSellingPrice ?? 0,
                    uomMapId: reqLatData?.mapId ?? null,
                  };
                  const taxableValue = Number(
                    reqLatData?.unitSellingPrice ?? 0,
                  );
                  if (taxableValue > 0) {
                    const taxPercent =
                      purchaseType === "Interstate"
                        ? gst
                        : Number(cgst) + Number(sgst);
                    const reqTaxes = Number(taxableValue * (taxPercent / 100));
                    updated.taxableValue = taxableValue;
                    updated.taxes = reqTaxes;
                    updated.itemTotal = taxableValue + reqTaxes;
                  }
                  return updated;
                }),
              }));
            }
          });
        }
        const errors = { ...(li.errors || {}) };
        const reqValue =
          typeof value === "object" ? (value?.value ?? "")?.toString() : value;
        switch (name) {
          case "name":
            if (!reqValue || reqValue?.trim() === "") {
              errors.name = "Product name is required";
            } else {
              delete errors.name;
            }
            break;

          case "quantity":
            if (!reqValue || Number(reqValue) <= 0) {
              errors.quantity = "Quantity must be greater than 0";
            } else {
              delete errors.quantity;
            }
            break;

          case "purchaseUom":
            if (!reqValue || reqValue?.trim() === "") {
              errors.purchaseUom = "UOM is required";
            } else {
              delete errors.purchaseUom;
            }
            break;

          case "gst":
            if (
              purchaseType === "Interstate" &&
              (!reqValue || Number(reqValue) < 0)
            ) {
              errors.gst = "GST must be 0 or positive";
            } else {
              delete errors.gst;
            }
            break;

          case "cgst":
          case "sgst":
            if (
              purchaseType !== "Interstate" &&
              (!reqValue || Number(reqValue) < 0)
            ) {
              errors[name] = `${name.toUpperCase()} must be 0 or positive`;
            } else {
              delete errors[name];
            }
            break;

          case "taxableValue":
            if (!reqValue || Number(reqValue) <= 0) {
              errors.taxableValue = "Taxable value must be positive";
            } else {
              delete errors.taxableValue;
            }
            break;

          case "itemTotal":
            if (!reqValue || Number(reqValue) <= 0) {
              errors.itemTotal = "Item total must be positive";
            } else {
              delete errors.itemTotal;
            }
            break;

          default:
            break;
        }
        if (
          name === "taxableValue" ||
          name === "sgst" ||
          name === "cgst" ||
          name === "gst" ||
          name === "quantity" ||
          name === "discountPercentage"
        ) {
          if (name === "sgst" || name === "cgst") {
            reqObj.cgst = value;
            reqObj.sgst = value;
            reqObj.gst = Number(value) * 2;
          }
          if (name === "gst") {
            reqObj.cgst = Number(value) / 2;
            reqObj.sgst = Number(value) / 2;
            reqObj.gst = value;
          }
          const reqQty = Number(
            name === "quantity" ? (value ?? 0) : (reqObj.quantity ?? 0),
          );
          const reqDiscountPer =
            name === "discountPercentage"
              ? (value ?? 0)
              : (reqObj.discountPercentage ?? 0);
          const reqTaxableValue =
            name === "taxableValue" ? Number(value) : Number(li?.taxableValue);
          const disValue = (reqTaxableValue / 100) * reqDiscountPer;
          const priceAfterDiscount = reqTaxableValue - disValue;
          const taxPercent =
            stckValue?.purchaseType?.value === "Interstate"
              ? Number(name === "gst" ? value : li?.gst)
              : Number(name === "cgst" || name === "sgst" ? value : li?.cgst) +
                Number(name === "sgst" || name === "cgst" ? value : li?.sgst);
          const reqTaxes = Number(priceAfterDiscount * (taxPercent / 100));
          reqObj.taxes = Number(reqTaxes ?? 0) * Number(reqQty ?? 0);
          reqObj.itemTotal =
            (Number(priceAfterDiscount) + Number(reqTaxes)) *
            Number(reqQty ?? 0);
        }
        if (name === "itemTotal") {
          const reqPer = (100 + Number(li?.gst)) / 100;
          const reqTaxableValue = Number(
            Number(value) / Number(reqPer),
          )?.toFixed(2);
          reqObj.taxes = Number(value) - reqTaxableValue;
          reqObj.taxableValue = reqTaxableValue;
          reqObj.itemTotal = value;
        }
        return { ...reqObj, errors };
      }
      return li;
    });
    setStckValue({ ...stckValue, list: reqList });
  };

  const handleAdd = () => {
    const reqList = [
      ...(stckValue?.list ?? []),
      {
        ...initObj,
        i: (stckValue?.list ?? [])?.length ?? 0,
        apiCalled: false,
        apiResponse: null,
      },
    ];
    setStckValue({ ...stckValue, list: reqList });
    setListNotValid(reqList?.length > 0 ? false : true);
  };

  const handleDeleteRow = (ind) => {
    const reqList = stckValue?.list
      ?.filter((li, i) => i !== ind)
      ?.map((li, i) => ({ ...li, i }));
    setStckValue({ ...stckValue, list: reqList });
    setListNotValid(reqList?.length > 0 ? false : true);
  };

  const validateStockList = (list, purchaseType) => {
    const errors = [];

    list?.forEach((item, index) => {
      const itemErrors = {};

      // Required fields
      if (!item?.name || item?.name?.trim() === "") {
        itemErrors.name = "Product name is required";
      }

      if (!item?.quantity || Number(item?.quantity) <= 0) {
        itemErrors.quantity = "Quantity must be greater than 0";
      }

      if (!item?.purchaseUom?.value) {
        itemErrors.purchaseUom = "UOM is required";
      }

      // Tax validation based on purchase type
      if (purchaseType !== "Exempt") {
        if (purchaseType === "Interstate") {
          if (
            item?.gst === undefined ||
            item?.gst === null ||
            item?.gst === "" ||
            Number(item?.gst) < 0
          ) {
            itemErrors.gst = "GST must be 0 or positive";
          }
        } else {
          if (
            item?.cgst === undefined ||
            item?.cgst === null ||
            item?.cgst === "" ||
            Number(item?.cgst) < 0
          ) {
            itemErrors.cgst = "CGST must be 0 or positive";
          }
          if (
            item?.sgst === undefined ||
            item?.sgst === null ||
            item?.sgst === "" ||
            Number(item?.sgst) < 0
          ) {
            itemErrors.sgst = "SGST must be 0 or positive";
          }
        }
      }

      // Validate item total
      if (!item?.itemTotal || Number(item?.itemTotal) <= 0) {
        itemErrors.itemTotal = "Item total must be greater than 0";
      }

      // Validate tax calculations
      const taxableValue = Number(item?.taxableValue) || 0;
      if (taxableValue <= 0) {
        itemErrors.taxableValue = "Taxable value must be greater than 0";
      }

      if (Object.keys(itemErrors).length > 0) {
        errors[index] = itemErrors;
      }
    });

    return errors;
  };

  const handleValidate = () => {
    return stckValue?.list?.filter((li) => {
      const nameEmpty = li?.name?.length === 0 || li?.name === "" || !li?.name;
      const noQty = Number(li?.quantity) < 1 || !Number(li?.quantity);
      const uomEmpty =
        li?.purchaseUom?.length === 0 ||
        li?.purchaseUom === "" ||
        !li?.purchaseUom;
      const noCgst = Number(li?.cgst) < 0;
      const noSgst = Number(li?.sgst) < 0;
      const totalEmpty =
        Number(li?.itemTotal) < 0 || !li?.itemTotal || li?.itemTotal === "";
      return nameEmpty || noQty || uomEmpty || noCgst || noSgst || totalEmpty;
    });
  };

  const handleManualValidationCheck = () => {
    let hlpLi = {},
      errLi = {};
    if (
      (stckValue?.supplierName === null || stckValue?.supplierName?.length) ===
      0
    ) {
      errLi["supplierName"] = true;
      hlpLi["supplierName"] = reqTxt;
    }
    if (
      stckValue?.invoiceNumber === null ||
      stckValue?.invoiceNumber?.length === 0
    ) {
      errLi["invoiceNumber"] = true;
      hlpLi["invoiceNumber"] = reqTxt;
    }
    if (stckValue?.billDate === null || stckValue?.billDate === undefined) {
      errLi = { ...errLi, billDate: true };
      hlpLi = { ...hlpLi, billDate: reqTxt };
    }

    setStckHelps({ ...stckHelps, ...hlpLi });
    setStckErrs({ ...stckErrs, ...errLi });
    return Object.values(errLi).some((item) => item);
  };

  const onStcApiCall = (typ) => {
    if (handleManualValidationCheck()) return;
    if (stckValue?.list?.length === 0 || !stckValue?.list) {
      return setListNotValid(true);
    } else {
      setListNotValid(false);
    }
    const listErrors = validateStockList(
      stckValue.list,
      stckValue.purchaseType?.value,
    );
    // Validate stock list

    if (listErrors.length > 0) {
      // Update state to show errors
      const updatedList = stckValue.list.map((item, index) => ({
        ...item,
        errors: listErrors[index] || {},
      }));
      setStckValue({ ...stckValue, list: updatedList });
      return setListNotValid(true);
    }

    if (handleValidate()?.length > 0 || !stckValue?.purchaseType) {
      return setListNotValid(true);
    } else {
      setListNotValid(false);
    }
    dispatch(showLoader());
    const data = {
      supplierDetials: {
        type: type,
        supplierName: stckValue?.supplierName?.toString() ?? "",
        supplierId:
          stckValue?.supplierId ?? selectedInvoice?.supplierId ?? null,
        GSTIN: stckValue?.gstin ?? "",
        email: stckValue?.email ?? "",
        phone: stckValue?.phone ?? "",
        address: stckValue?.address ?? "",
        date: moment(new Date(stckValue?.billDate)).format("YYYY-MM-DD"),
        // expectedDeliveryDate: moment(
        //   new Date(stckValue?.expectedDelivery),
        // ).format("YYYY-MM-DD"),
        purchaseType: stckValue?.purchaseType?.value,
        poNumber: stckValue?.poNumber ?? "",
        invoiceNumber: stckValue?.invoiceNumber ?? "",
        grnNumber: stckValue?.grnNumber ?? "",
        creditSave: typ,
        totalTaxableValue:
          getTotalValues({
            list: stckValue?.list,
            additionalCharges: stckValue?.additionalCharges,
          })?.taxableValues ?? 0,
        totalTaxes:
          getTotalValues({
            list: stckValue?.list,
            additionalCharges: stckValue?.additionalCharges,
          })?.taxes ?? 0,
        additionalCharges: Number(stckValue?.additionalCharges) ?? 0,
        grandTotal: Number(
          getTotalValues({
            list: stckValue?.list,
            additionalCharges: stckValue?.additionalCharges,
          })?.grandTotal ?? 0,
        ),
        invoiceDetails: stckValue?.list?.map((itm) => {
          return {
            invoiceDetailId: itm?.invoiceDetailId ?? null,
            hsnSacCode: itm?.hsnSacCode ?? itm?.HSNSAC ?? "",
            name: itm?.name ?? itm?.Name ?? "",
            tradeName: itm?.tradeName ?? itm?.name ?? itm?.Name ?? "",
            stockId: itm?.stockId ?? null,
            orderedQuantity: itm?.orderedQuantity ?? null,
            quantity: Number(getExactNumber(itm?.quantity)) ?? 1,
            uom: itm?.purchaseUom?.value ?? null,
            uomId: itm?.uomMapId ?? null,
            cgst:
              stckValue?.purchaseType?.value === "Intrastate"
                ? Number(Number(itm?.gst ?? itm?.igst ?? 0) / 2)
                : 0,
            sgst:
              stckValue?.purchaseType?.value === "Intrastate"
                ? Number(Number(itm?.gst ?? itm?.igst ?? 0) / 2)
                : 0,
            igst:
              stckValue?.purchaseType?.value === "Interstate"
                ? Number(itm?.igst ?? 0)
                : 0,
            batchNumber: itm?.batchNumber ?? "",
            mrp: Number(itm?.mrp ?? 0),
            discountPercentage:
              Number(itm?.discountPercentage) > 0
                ? Number(itm?.discountPercentage)
                : 0,
            expiryDate: itm?.expiryDate
              ? moment(new Date(itm?.expiryDate)).format("YYYY-MM-DD")
              : null,
            taxableValue: Number(itm?.taxableValue) ?? 0,
            taxes: Number(itm?.taxes) ?? 0,
            itemTotal: Number(itm?.itemTotal) ?? 0,
            landedCost:
              Number(
                (Number(itm?.itemTotal) ?? 0) /
                  (Number(getExactNumber(itm?.quantity)) ?? 1),
              ) ?? 0,
          };
        }),
      },
    };
    try {
      (selectedInvoice?.detailId
        ? dispatch(
            updateStockDetailsById({ data, id: selectedInvoice?.detailId }),
          )
        : dispatch(createOrderInvoice(data))
      ).then((res) => {
        if (res?.payload) {
          onCloseMod("apiSuccess");
          setStcEntModVsble(false);
          setStckValue(initStckVal);
          setStckErrs({});
          setStckHelps({});
          setActStep(1);
          setSubmitErr(false);
        }
      });
    } catch (err) {
      dispatch(hideLoader());
      console.log("Err");
    }
  };

  const onStcEntUpload = async () => {
    if (stcEntFiles?.length === 0 || !stcEntFiles) return setListNotValid(true);
    dispatch(showLoader());
    const form = new FormData();
    stcEntFiles?.forEach((fle) => {
      form.append("files", fle?.file);
    });

    try {
      const apiRes = await soapApi.post("/purchase", form);
      if (apiRes?.status === 200) {
        const reqDta = apiRes?.data;
        const reqList = reqDta?.Items?.map((itm, i) => {
          const reqCgst =
            reqDta?.Purchasetype === "Intrastate"
              ? Number(itm?.GST ?? 0) / 2
              : 0;
          const reqSgst =
            reqDta?.Purchasetype === "Intrastate"
              ? Number(itm?.GST ?? 0) / 2
              : 0;
          const reqUom = administeredUOMList?.find(
            (al) =>
              al?.value?.toLowerCase() ===
              (itm?.Purchaseuom ?? "")?.toLowerCase(),
          );
          return {
            ...itm,
            hsnSacCode: itm?.HSNSAC,
            name: itm?.Name,
            quantity: itm?.Quantity,
            cgst: reqCgst,
            sgst: reqSgst,
            gst: Number(itm?.GST ?? 0),
            igst: Number(itm?.GST ?? 0),
            taxes: itm?.Taxes ?? 0,
            itemTotal: itm?.Itemtotal ?? 0,
            landedCost: itm?.landedCost ?? 0,
            purchaseUom: reqUom ? reqUom : null,
            taxableValue: itm?.Taxablevalue ?? 0,
            i,
            apiCalled: false,
            apiResponse: null,
          };
        });
        // const reqSupplier = suppliers?.data?.find((sup) => sup?.supplierName?.toLowerCase() === reqDta?.Suppliername?.toLowerCase());
        const reqObj = {
          ...reqDta,
          additionalCharges: getExactNumber(reqDta?.Additionalcharges),
          address: reqDta?.Address,
          billDate:
            reqDta?.Billdate?.length > 0
              ? new Date(normalizeDate(reqDta?.Billdate))
              : new Date(),
          email: reqDta?.Email,
          GSTIN: reqDta?.GSTIN,
          grandtotal: reqDta?.Grandtotal,
          invoiceNumber: reqDta?.Invoicenumber,
          phone: reqDta?.Phone ?? "",
          supplierName: reqDta?.Suppliername,
          //  reqSupplier ? { name: reqSupplier?.supplierName, value: reqSupplier?.supplierName, supplierId: reqSupplier?.supplierId } : null,          ,
          totaltaxablevalue: reqDta?.Totaltaxablevalue ?? 0,
          totalTaxes: reqDta?.Totaltaxes ?? 0,
          list: reqList,
          purchaseType: {
            name: reqDta?.Purchasetype,
            value: reqDta?.Purchasetype,
          },
        };
        setStcEntFiles([]);
        setStckValue(reqObj);
        setActStep(2);
        setStockEntErr(false);
        dispatch(hideLoader());
      } else {
        dispatch(hideLoader());
      }
    } catch (err) {
      console.log("err", err);
      dispatch(hideLoader());
    }
  };

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={() => onCloseMod("close")}
        header={title}
        headerCenter
        modalWidth={actStep === 1 ? 40 : 90}
        modal
      >
        <div className="scroll-90vh">
          <Grid container spacing={0} className="pb20">
            {actStep === 1 ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="flex-row-ali-cen flex-center">
                  <Typography className="txt-semi-bold fs16 black">
                    Step {actStep}:
                  </Typography>
                  <Typography className="ml5 font-medium fs16 black">
                    Upload a clear picture of your supplier invoice
                  </Typography>
                </div>
              </Grid>
            ) : null}
            {actStep === 1 ? (
              <>
                {stcEntFiles?.length > 0 ? (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="mt20"
                  >
                    <FilesWithCloseButton
                      files={stcEntFiles}
                      onClickFile={(i, fle) => handleImageRemove(i)}
                      center
                    />
                  </Grid>
                ) : null}
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="p20"
                >
                  <CustomUpload
                    onUploadFile={onEntFileUpload}
                    multipleNew
                    fileAccept="image/*,.pdf"
                    multiFiles
                  />
                </Grid>
                {stcEntFiles?.length > 0 || hideManualEntry ? null : (
                  <div className="flex1-center">
                    <div className="mb20">
                      <CustomButton
                        text="Manual Entry"
                        smallBtn
                        onClick={handleManualAddEntry}
                        tabOutlined
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <Grid container spacing={2} className="mv10">
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomSelect
                        list={getSupplierList()}
                        handleChange={(e, value) => {
                          handleChangeStckValue(
                            "supplierName",
                            e?.value ?? e,
                            e,
                          );
                        }}
                        value={stckValue?.supplierName}
                        label="Supplier Name *"
                        error={stckErrs?.supplierName}
                        helperText={stckHelps?.supplierName}
                        // search
                        addNewSelect
                        plusBtn
                        addSearchDirectly
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomTextField
                        name="GSTIN"
                        fullWidth
                        handleChange={(e) =>
                          handleChangeStckValue("GSTIN", e?.target?.value)
                        }
                        value={stckValue?.GSTIN}
                        label="GSTIN"
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomTextField
                        name="email"
                        fullWidth
                        handleChange={(e) =>
                          handleChangeStckValue("email", e?.target?.value)
                        }
                        value={stckValue?.email}
                        label="Email"
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomTextField
                        name="phone"
                        fullWidth
                        handleChange={(e) =>
                          handleChangeStckValue("phone", e?.target?.value)
                        }
                        value={stckValue?.phone}
                        label="Phone"
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomTextField
                        name="address"
                        fullWidth
                        handleChange={(e) =>
                          handleChangeStckValue("address", e?.target?.value)
                        }
                        value={stckValue?.address}
                        multiline
                        rows={3}
                        label="Address"
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomTextField
                        name="invoiceNumber"
                        fullWidth
                        handleChange={(e) =>
                          handleChangeStckValue(
                            "invoiceNumber",
                            e?.target?.value,
                          )
                        }
                        value={stckValue?.invoiceNumber}
                        label="Invoice Number"
                        error={stckErrs?.invoiceNumber}
                        helperText={stckHelps?.invoiceNumber}
                        required={true}
                      />
                    </div>
                  </Grid>
                  {/* <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomTextField
                        name="grnNumber"
                        fullWidth
                        handleChange={(e) =>
                          handleChangeStckValue("grnNumber", e?.target?.value)
                        }
                        value={stckValue?.grnNumber}
                        label="GRN number"
                      />
                    </div>
                  </Grid> */}
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomTextField
                        name="date"
                        fullWidth
                        handleChange={(e) =>
                          handleChangeStckValue("billDate", e?.target?.value)
                        }
                        value={stckValue?.billDate}
                        mobileDateWithBatch
                        label="Date *"
                        required={true}
                        error={stckErrs?.billDate}
                        helperText={stckHelps?.billDate}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <div class="mb20">
                      <CustomSelect
                        list={purchaseTypes}
                        handleChange={(e, value) =>
                          handleChangeStckValue("purchaseType", value)
                        }
                        value={stckValue?.purchaseType}
                        label="Purchase Type"
                        search
                      />
                    </div>
                  </Grid>
                </Grid>

                <TableContainer sx={{ maxHeight: 300 }} className="mb20">
                  <Table
                    sx={{ minWidth: 200 }}
                    aria-labelledby="tableTitle"
                    stickyHeader
                  >
                    <TableHead>
                      <TableRow>
                        {(stckValue?.purchaseType?.value === "Interstate"
                          ? stckEntIgstTblHdrs
                          : stckEntTblHdrs
                        )?.map((hdr, i) => (
                          <TableCell
                            style={tblGrayBack}
                            className={`table-header-text`}
                          >
                            <div
                              style={{
                                width:
                                  hdr === "sno"
                                    ? 30
                                    : hdr === "hsnSacCode"
                                      ? 120
                                      : hdr === "name"
                                        ? 200
                                        : hdr === "quantity" ||
                                            hdr === "samples" ||
                                            hdr === "cgst" ||
                                            hdr === "sgst" ||
                                            hdr === "gst" ||
                                            i ===
                                              (stckValue?.purchaseType
                                                ?.value === "Interstate"
                                                ? stckEntIgstTblHdrs
                                                : stckEntTblHdrs
                                              )?.length -
                                                1
                                          ? 70
                                          : hdr === "purchaseUom"
                                            ? 100
                                            : hdr === "discounts"
                                              ? 80
                                              : 130,
                              }}
                            >
                              {stckNmeExpan?.[hdr]}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stckValue?.list?.map((li, ind) => {
                        return (
                          <TableRow>
                            {(stckValue?.purchaseType?.value === "Interstate"
                              ? stckEntIgstTblHdrs
                              : stckEntTblHdrs
                            )?.map((hdr, i) => (
                              <TableCell
                                key={ind + `${i}` + "tr"}
                                component="th"
                                sx={{ verticalAlign: "top" }}
                              >
                                <div
                                  style={{
                                    width:
                                      hdr === "sno"
                                        ? 30
                                        : hdr === "hsnSacCode"
                                          ? 120
                                          : hdr === "name"
                                            ? 200
                                            : hdr === "quantity" ||
                                                hdr === "samples" ||
                                                hdr === "cgst" ||
                                                hdr === "sgst" ||
                                                hdr === "gst" ||
                                                i ===
                                                  (stckValue?.purchaseType
                                                    ?.value === "Interstate"
                                                    ? stckEntIgstTblHdrs
                                                    : stckEntTblHdrs
                                                  )?.length -
                                                    1
                                              ? 70
                                              : hdr === "purchaseUom"
                                                ? 100
                                                : hdr === "discounts"
                                                  ? 80
                                                  : 130,
                                  }}
                                >
                                  {i ===
                                  (stckValue?.purchaseType?.value ===
                                  "Interstate"
                                    ? stckEntIgstTblHdrs
                                    : stckEntTblHdrs
                                  )?.length -
                                    1 ? (
                                    <img
                                      src={require("../../../assets/images/png/delete.png")}
                                      className="imghw30 cursor mt5"
                                      onClick={() => handleDeleteRow(ind)}
                                    />
                                  ) : li?.apiCalled ? (
                                    hdr === "name" ? (
                                      <Custom3dotString
                                        str={li?.[hdr]}
                                        className="font-regular fs14"
                                        length={13}
                                      />
                                    ) : (
                                      (li?.product?.[hdr] ?? li?.[hdr])
                                    )
                                  ) : hdr === "sno" ? (
                                    ind + 1
                                  ) : hdr === "name" ? (
                                    <CustomSelect
                                      list={getStockList()}
                                      handleChange={(e, value) => {
                                        handleChangeTableValue(
                                          hdr,
                                          e?.value ?? e,
                                          ind,
                                          e,
                                        );
                                      }}
                                      value={li?.[hdr]}
                                      label=""
                                      error={!!li?.errors?.[hdr]}
                                      helperText={li?.errors?.[hdr]}
                                      addNewSelect
                                      plusBtn
                                      addSearchDirectly
                                    />
                                  ) : hdr === "purchaseUom" ? (
                                    <CustomSelect
                                      list={administeredUOMList}
                                      value={li?.[hdr]}
                                      handleChange={(e, value) =>
                                        handleChangeTableValue(hdr, value, ind)
                                      }
                                      name="uom"
                                      search
                                      error={!!li?.errors?.[hdr]}
                                      helperText={li?.errors?.[hdr]}
                                    />
                                  ) : (
                                    <CustomTextField
                                      name={hdr}
                                      fullWidth
                                      handleChange={(e) =>
                                        handleChangeTableValue(
                                          hdr,
                                          e?.target?.value,
                                          ind,
                                        )
                                      }
                                      error={!!li?.errors?.[hdr]}
                                      helperText={li?.errors?.[hdr]}
                                      value={
                                        hdr === "taxes"
                                          ? Number(li?.[hdr])?.toFixed(2)
                                          : li?.[hdr]
                                      }
                                      disabled={
                                        hdr === "itemTotal" ||
                                        hdr === "taxes" ||
                                        (stckValue?.purchaseType?.value ===
                                        "Exempt"
                                          ? hdr === "cgst" || hdr === "sgst"
                                          : false)
                                      }
                                      type={
                                        hdr === "hsnSacCode" ||
                                        hdr === "name" ||
                                        hdr === "purchaseUom" ||
                                        hdr === "batchNumber"
                                          ? "string"
                                          : "number"
                                      }
                                      mobileDate={hdr === "expiryDate"}
                                    />
                                  )}
                                </div>
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <div>
                  <CustomButton text="Add" smallBtn onClick={handleAdd} />
                </div>
                <div className="flex1-end pr50">
                  <div className="w25Per">
                    <div className="flex-row-ali-cen">
                      <div className="w60Per">
                        <div className="font-medium fs14 black">
                          Total Taxable Value
                        </div>
                      </div>
                      <div className="flex1-end pr10">
                        {Number(
                          getTotalValues({
                            list: stckValue?.list,
                            additionalCharges: stckValue?.additionalCharges,
                          })?.taxableValues -
                            getTotalValues({
                              list: stckValue?.list,
                              additionalCharges: stckValue?.additionalCharges,
                            })?.discount,
                        )?.toFixed(2)}
                      </div>
                    </div>
                    <div className="flex-row-ali-cen mt10">
                      <div className="w60Per">
                        <div className="font-medium fs14 black">
                          Discount Value
                        </div>
                      </div>
                      <div className="flex1-end pr10">
                        {Number(
                          getTotalValues({
                            list: stckValue?.list,
                            additionalCharges: stckValue?.additionalCharges,
                          })?.discount,
                        )?.toFixed(2)}
                      </div>
                    </div>
                    {stckValue?.purchaseType?.value === "Interstate" ? (
                      <div className="flex-row-ali-cen mt10">
                        <div className="w60Per">
                          <div className="font-medium fs14 black">
                            Taxes (IGST)
                          </div>
                        </div>
                        <div className="flex1-end pr10">
                          {Number(
                            getTotalValues({
                              list: stckValue?.list,
                              additionalCharges: stckValue?.additionalCharges,
                            })?.taxes,
                          )?.toFixed(2)}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex-row-ali-cen mt10">
                          <div className="w60Per">
                            <div className="font-medium fs14 black">
                              Taxes (CGST)
                            </div>
                          </div>
                          <div className="flex1-end pr10">
                            {Number(
                              Number(
                                getTotalValues({
                                  list: stckValue?.list,
                                  additionalCharges:
                                    stckValue?.additionalCharges,
                                })?.taxes,
                              ) / 2,
                            )?.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex-row-ali-cen mt10">
                          <div className="w60Per">
                            <div className="font-medium fs14 black">
                              Taxes (SGST)
                            </div>
                          </div>
                          <div className="flex1-end pr10">
                            {Number(
                              Number(
                                getTotalValues({
                                  list: stckValue?.list,
                                  additionalCharges:
                                    stckValue?.additionalCharges,
                                })?.taxes,
                              ) / 2,
                            )?.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex-row-ali-cen mt10">
                      <div className="w60Per">
                        <div className="font-medium fs14 black">
                          Additional Charges
                        </div>
                      </div>
                      <div className="flex1-end">
                        <CustomTextField
                          fullWidth
                          handleChange={(e) =>
                            handleChangeAdditionalCharge(
                              "additionalCharges",
                              e?.target?.value,
                            )
                          }
                          value={stckValue?.additionalCharges}
                          type="number"
                          textRightAlign
                        />
                      </div>
                    </div>
                    <div className="flex-row-ali-cen mt10">
                      <div className="flex-start">
                        <div className="font-medium fs14 black">
                          Grand Total
                        </div>
                      </div>
                      <div className="flex1-end pr10">
                        <div className="font-bold fs14 black">
                          {Number(
                            getTotalValues({
                              list: stckValue?.list,
                              additionalCharges: stckValue?.additionalCharges,
                            })?.grandTotal,
                          )?.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              {actStep === 1 ? (
                stockEntrErr ? (
                  <div className="flex-center mb10">
                    <FormHelperText error>
                      Unable to process the request. Kindly upload a clear
                      picture and try again.
                    </FormHelperText>
                  </div>
                ) : null
              ) : submitErr ? (
                <div className="flex-center mb10">
                  <FormHelperText error>
                    Please upload images against each product to complete the
                    process or skip to continue later.
                  </FormHelperText>
                </div>
              ) : null}

              {isListNotValid ? (
                <div className="flex-center mb10">
                  <FormHelperText error>
                    Please add at least 1 record
                  </FormHelperText>
                </div>
              ) : null}

              <div className="flex-center">
                <div className="flex-row">
                  {actStep === 2 ? (
                    <div>
                      <CustomButton
                        text="Save As Draft"
                        smallBtn
                        onClick={() => onStcApiCall("Draft")}
                      />
                    </div>
                  ) : null}
                  <div className={actStep === 1 ? "" : "ml15"}>
                    <CustomButton
                      text="Submit"
                      smallBtn
                      onClick={() => {
                        actStep === 1
                          ? onStcEntUpload()
                          : onStcApiCall("Submitted");
                      }}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default PurchaseInvoiceCreateView;
